<script>
import GITLAB_LOGO_SVG from '@gitlab/svgs/dist/illustrations/gitlab_logo.svg';
import { GlButton } from '@gitlab/ui';
import { MountingPortal } from 'portal-vue';
import csrf from '~/javascripts/lib/csrf';
import Tracking from '~/javascripts/tracking';
import {
  TRACKING_ACTIONS,
  TRACKING_CATEGORIES,
  TRACKING_LABELS,
} from '~/javascripts/tracking/constants';
import { __, sprintf } from '~/javascripts/lib/locale';
import FooterApp from './footer_app.vue';

export default {
  name: 'SignInApp',
  components: {
    GlButton,
    FooterApp,
    MountingPortal,
  },
  props: {
    ssoUrl: {
      type: String,
      required: true,
    },
    gitlabSignUpUrl: {
      type: String,
      required: false,
      default: null,
    },
    legacyAccountSignInUrl: {
      type: String,
      required: true,
    },
    productToPurchase: {
      type: String,
      required: false,
      default: null,
    },
    showRegisterOnGitlabCta: {
      type: Boolean,
      required: false,
      default: false,
    },
    referrerLocation: {
      type: String,
      required: false,
      default: undefined,
    },
    autoSubmitSso: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      csrfToken: csrf.token,
    };
  },
  GITLAB_LOGO_SVG,
  computed: {
    title() {
      return this.productToPurchase
        ? sprintf(__('Buy %{product}'), { product: this.productToPurchase })
        : __('Sign in');
    },
    hiddenJH() {
      return !gon.global?.jh;
    },
  },
  created() {
    this.tracking = new Tracking();
  },
  mounted() {
    if (this.autoSubmitSso) {
      this.submitSsoForm();
    }
  },
  methods: {
    submitSsoForm() {
      this.$refs.ssoForm.submit();
    },
    trackSignInCtaClick() {
      this.tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.webstoreSigninCtaClick, {
        label: TRACKING_LABELS.signInWithExistingAccount,
        property: this.referrerLocation,
      });
    },
    trackRegisterCtaClick() {
      this.tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.webstoreSigninCtaClick, {
        label: TRACKING_LABELS.registerForNewAccount,
        property: this.referrerLocation,
      });
    },
  },
  i18n: {
    signInToCompletePurchase: __('Please sign in to complete your purchase'),
    signInToCompletePurchaseDesc: __(
      'The Customers Portal is a secure site for managing GitLab subscriptions, billing contacts, payments, and license details, accessed with your GitLab.com account.',
    ),
    signInWithGitlabAccount: __('Sign in with Jihulab.com account'),
  },
};
</script>

<template>
  <div class="gl-m-auto gl-my-8 gl-text-center">
    <div class="gl-max-w-48 gl-m-auto">
      <h2 class="gl-font-bold" data-testid="title">{{ title }}</h2>
      <p v-if="productToPurchase" class="gl-font-bold gl-mt-5">
        {{ $options.i18n.signInToCompletePurchase }}
      </p>
      <p class="gl-mt-5">{{ $options.i18n.signInToCompletePurchaseDesc }}</p>
    </div>
    <form ref="ssoForm" method="post" :action="ssoUrl">
      <input name="utf8" type="hidden" value="✓" />
      <input type="hidden" name="authenticity_token" :value="csrfToken" />

      <div class="gl-flex gl-flex-wrap gl-gap-3 gl-justify-center gl-mt-8">
        <gl-button
          type="submit"
          data-testid="sign-in-with-gitlab-button"
          @click="trackSignInCtaClick"
        >
          <span>
            <img class="gl-w-5" :src="$options.GITLAB_LOGO_SVG" alt="GitLab Logo" />
            {{ $options.i18n.signInWithGitlabAccount }}
          </span>
        </gl-button>
        <gl-button
          v-if="showRegisterOnGitlabCta && hiddenJH"
          target="_blank"
          category="primary"
          :href="gitlabSignUpUrl"
          data-testid="register-on-gitlab-button"
          @click="trackRegisterCtaClick"
        >
          Register new GitLab.com account
        </gl-button>
      </div>
    </form>
    <mounting-portal v-if="hiddenJH" mount-to="#js-footer" slim>
      <footer-app :legacy-account-sign-in-url="legacyAccountSignInUrl" />
    </mounting-portal>
  </div>
</template>
